import React, { useRef } from 'react';
import { useDrop , useDrag } from 'react-dnd';
import { CheckCircleIcon, AlertCircle, X } from 'lucide-react';
import { useTheme } from '../../../../../hooks/useTheme';

const ItemType = 'ITEM';

// ============================================================
// DRAGGABLE ITEM
// ============================================================
const DraggableItem = ({ item, sourceBoxId, deleteItem }) => {
  const { theme } = useTheme();

  const [{ isDragging }, drag] = useDrag({  // ← THIS WAS MISSING
    type: ItemType,
    item: { item, sourceBoxId },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const truncatedText = item.length > 50 ? item.substring(0, 50) + '....' : item;

  return (
    <div
      ref={drag}  // ← THIS WAS MISSING
      className={`${
        sourceBoxId === 'required'
          ? `${theme === 'dark' ? 'secondary-button' : 'secondary-button-light'}`
          : 'gradient-transparent-button'
      } cursor-pointer py-2 px-3 dark:text-white text-textSecondary rounded-full text-[13px] flex items-center space-x-2 transition-all duration-300 ${
        isDragging ? 'opacity-50 scale-95' : 'opacity-100'
      }`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      title={item}
    >
      <span className={`dark:text-white ${sourceBoxId !== 'aiSuggestedSkills' ? 'text-white' : 'text-textSecondary'} z-10`}>
        {truncatedText}
      </span>
      {sourceBoxId !== 'aiSuggestedSkills' && (
        <button
          onClick={(e) => { e.stopPropagation(); deleteItem(item, sourceBoxId); }}
          className="ml-2 group relative"
        >
          <X
            className={`w-4 h-4 relative z-10 ${sourceBoxId !== 'aiSuggestedSkills' ? 'text-gray-200' : 'text-textSecondary'} rounded-xl transition-transform`}
            strokeWidth={2.5}
          />
        </button>
      )}
    </div>
  );
};

// ============================================================
// DRAGGABLE BOX
// ============================================================
const DraggableBox = ({
  id,
  name,
  items,
  handleDrop,
  addSkillInput = null,
  deleteItem,
}) => {
  const [{ canDrop }, drop] = useDrop({
    accept: ItemType,
    drop: (draggedItem, monitor) =>
      id !== 'aiSuggestedSkills' &&
      handleDrop(draggedItem, id, calculateTargetIndex(monitor.getClientOffset())),
    canDrop: () => id !== 'aiSuggestedSkills',
  });

  const calculateTargetIndex = (clientOffset) => {
    if (!clientOffset) return items.length;
    const bounds = document.getElementById(id).getBoundingClientRect();
    const offsetY = clientOffset.y - bounds.top;
    return items.reduce((acc, _, i) => (offsetY <= 30 * (i + 1) ? acc : i + 1), 0);
  };

  return (
    <div className="relative">
      <label className="absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1">
        {name}
      </label>
      <div
        id={id}
        ref={drop}
        className={`w-full px-4 pb-4 pt-5 mt-2 bg-transparent border rounded-lg transition-all duration-300 ${
          canDrop
            ? 'border-purple-400 focus:ring-2 focus:ring-purple-400 focus:border-transparent'
            : 'border-[#5A4479] focus:ring-2 focus:ring-[#5A4479]'
        }`}
        style={{ height: '420px' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && name !== 'AI Suggested Skills' ? (
              <p className="text-2xl dark:font-medium font-normal text-center mt-36 dark:text-gray-500 text-gray-300 ml-2 text-opacity-60">
                You can drop the skills here
              </p>
            ) : (
              <ul className="flex flex-wrap gap-[5px]">
                {items.map((item, index) => (
                  <li key={index} className="flex-shrink-0 p-[1px]">
                    <DraggableItem
                      item={item}
                      index={index}
                      sourceBoxId={id}
                      deleteItem={deleteItem}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          {addSkillInput && (
            <div className="mt-2 flex gap-2 items-center flex-shrink-0">
              {addSkillInput}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// NAVIGATION BUTTONS
// ============================================================
const NavigationButtons = ({ onBack, onNext, nextLabel = 'Next', nextDisabled = false, showBackButton = true, showborderbtn = false }) => {
  const { theme } = useTheme();

  return (
    <div className="flex justify-between items-center w-full py-4">
      {showBackButton ? (
        <button
          onClick={onBack}
          className={`${showborderbtn && theme !== 'dark' ? 'border-btn !text-textSecondary' : 'button'} flex items-center gap-2 rounded-full dark:!text-white dark:border dark:border-gray-600 hover:border-gray-400 transition-all duration-200`}
        >
          <span>Back</span>
        </button>
      ) : <div></div>}
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className={`button px-6 py-3 rounded-full text-white ${nextDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span>{nextLabel}</span>
      </button>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
function SmartStep2_SelectSkills({
  formData,
  setFormData,
  updateFormData,
  handleDrop,
  deleteItem,
  customRequiredSkillRef,
  onAddCustomSkill,
  selectedStyle,
  getDefaultTimeForSkills,
  setCurrentStep,
  setCurrentSubStep,
  handleGenerateJD,
}) {
  const { theme } = useTheme(); // ← pulled from context, not prop

  return (
    <div className="w-full px-12">
      <div className="space-y-4 mb-4">
        <h2 className="text-lg font-light dark:text-white text-textPrimary">
          Now, select the skills you find relevant from the AI-suggested  list{' '}
          <span className="text-md dark:text-gray-400 text-textSecondary">(Drag & Drop)</span>
        </h2>

        {formData.errors.requiredSkills && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 w-[50%]">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-500/90 leading-relaxed">
              Please add at least 3 required skills.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <DraggableBox
            id="aiSuggestedSkills"
            name="AI Suggested Skills"
            items={formData.aiSuggestedSkills}
            setItems={(items) => setFormData({ ...formData, aiSuggestedSkills: items })}
            handleDrop={handleDrop}
            deleteItem={deleteItem}
          />

          <DraggableBox
            id="required"
            name="Required Skills for an interview"
            items={formData.requiredSkills}
            setItems={(items) => setFormData({ ...formData, requiredSkills: items })}
            handleDrop={handleDrop}
            deleteItem={deleteItem}
            addSkillInput={
              <div className="relative w-full">
                <p className="px-2 mb-1 text-sm dark:text-gray-400 text-textSecondary ml-2">
                  Didn't find relevant skills? Add your own
                </p>
                <div className={`w-[95%] pr-2 flex flex-row items-center justify-between ${theme === 'dark' ? 'glass-effect focus:ring-[#5A4479]' : 'border border-gray-300 focus:border-gray-400'} rounded-full transition`}>
                  <input
                    type="text"
                    placeholder="Type a Skill..for example: Program Management"
                    defaultValue=""
                    ref={customRequiredSkillRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customRequiredSkillRef.current.value.trim()) {
                        onAddCustomSkill();
                      }
                    }}
                    className={`${theme === 'dark' ? 'bg-transparent focus:ring-0 focus:outline-none' : 'bg-bgSecondary focus:border-gray-400 focus:ring-0 focus:outline-none'} placeholder:text-sm dark:placeholder:text-gray-500 placeholder:italic dark:text-white text-textSecondary w-11/12 pl-4 pr-14 py-3 rounded-full transition`}
                  />
                  <button
                    onClick={onAddCustomSkill}
                    className={`${theme === 'dark' ? 'button' : 'border-btn'} text-[13px] min-w-[25px] px-4 py-2.5 text-white transition-transform flex items-center justify-center`}
                  >
                    <span className="text-textPrimary">Add</span>
                  </button>
                </div>
              </div>
            }
          />
        </div>
      </div>

      <NavigationButtons
        onBack={() => {
          setCurrentStep(1);
          setCurrentSubStep(2);
          updateFormData({ interview_timelimit: null });
        }}
        onNext={async () => {
          const success = await handleGenerateJD();
          if (success) {
            if (selectedStyle !== 'screening' && !formData.isInterviewTimeManuallySet) {
              const requiredCount = formData.requiredSkills?.length || 0;
              const optionalCount = formData.optionalSkills?.length || 0;
              const totalSkillCount = requiredCount + optionalCount;
              const defaultTime = getDefaultTimeForSkills(totalSkillCount);
              updateFormData({ interview_timelimit: defaultTime });
            }
            setCurrentStep(3);
            setCurrentSubStep(1);
          }
        }}
        showborderbtn={true}
      />
    </div>
  );
}

export default SmartStep2_SelectSkills;